import { useQuery } from '@tanstack/react-query';
import { UserRole, UserStatus, PropertyStatus } from '@shared/schema';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Building2, Users, UserCheck, Clock, CheckCircle2, 
  DollarSign, PercentIcon, CalendarClock, AreaChart 
} from 'lucide-react';
import { Link } from 'wouter';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/use-auth';
import { Badge } from '@/components/ui/badge';

export default function AdminDashboard() {
  const { user } = useAuth();
  
  // Fetch properties for stats
  const { data: properties = [] } = useQuery({
    queryKey: ['admin/properties'],
    enabled: !!user && user.role === UserRole.ADMIN,
  });
  
  // Fetch users for stats
  const { data: owners = [] } = useQuery({
    queryKey: ['admin/users/owner'],
    enabled: !!user && user.role === UserRole.ADMIN,
  });
  
  const { data: agents = [] } = useQuery({
    queryKey: ['admin/users/agent'],
    enabled: !!user && user.role === UserRole.ADMIN,
  });
  
  // Calculate counts
  const pendingProperties = properties.filter(p => p.status === PropertyStatus.PENDING).length;
  const activeProperties = properties.filter(p => p.status === PropertyStatus.ACTIVE).length;
  const rejectedProperties = properties.filter(p => p.status === PropertyStatus.REJECTED).length;
  
  const pendingOwners = owners.filter(o => o.status === UserStatus.PENDING).length;
  const approvedOwners = owners.filter(o => o.status === UserStatus.APPROVED).length;
  
  const pendingAgents = agents.filter(a => a.status === UserStatus.PENDING).length;
  const approvedAgents = agents.filter(a => a.status === UserStatus.APPROVED).length;

  return (
    <AdminLayout>
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold">Admin Dashboard</h1>
        <p className="text-muted-foreground">Manage your platform</p>
      </div>
      
      {/* Key Metrics - Mobile optimized grid */}
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        {/* Card 1: Total Properties */}
        <Card className="col-span-1">
          <CardHeader className="flex flex-row items-center justify-between p-3 pb-0 space-y-0">
            <CardTitle className="text-sm font-medium">Total Properties</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent className="p-3 pt-2">
            <div className="text-xl md:text-2xl font-bold">{properties.length}</div>
            <div className="flex flex-wrap items-center gap-1 mt-1">
              <Badge variant="outline" className="bg-green-50 text-green-700 hover:bg-green-50 text-xs">
                {activeProperties} Active
              </Badge>
              <Badge variant="outline" className="bg-yellow-50 text-yellow-700 hover:bg-yellow-50 text-xs">
                {pendingProperties} Pending
              </Badge>
            </div>
          </CardContent>
        </Card>
        
        {/* Card 2: Pending Properties */}
        <Card className="col-span-1">
          <CardHeader className="flex flex-row items-center justify-between p-3 pb-0 space-y-0">
            <CardTitle className="text-sm font-medium">Pending Properties</CardTitle>
            <Clock className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent className="p-3 pt-2">
            <div className="text-xl md:text-2xl font-bold">{pendingProperties}</div>
            <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
              {pendingProperties > 0 ? 'Awaiting your review' : 'All clear'}
            </p>
            {pendingProperties > 0 && (
              <Button asChild variant="outline" size="sm" className="mt-2 w-full text-xs py-1 h-auto">
                <Link href="/admin/properties?status=pending">Review</Link>
              </Button>
            )}
          </CardContent>
        </Card>
        
        {/* Card 3: Total Bookings (Phase 2) */}
        <Card className="col-span-1">
          <CardHeader className="flex flex-row items-center justify-between p-3 pb-0 space-y-0">
            <CardTitle className="text-sm font-medium">Total Bookings</CardTitle>
            <CalendarClock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent className="p-3 pt-2">
            <div className="text-xl md:text-2xl font-bold">-</div>
            <div className="flex items-center mt-1">
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-50 text-xs">
                Phase 2
              </Badge>
            </div>
          </CardContent>
        </Card>
        
        {/* Card 4: Total Revenue (Phase 2) */}
        <Card className="col-span-1 hidden sm:block">
          <CardHeader className="flex flex-row items-center justify-between p-3 pb-0 space-y-0">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent className="p-3 pt-2">
            <div className="text-xl md:text-2xl font-bold">-</div>
            <div className="flex items-center mt-1">
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-50 text-xs">
                Phase 2
              </Badge>
            </div>
          </CardContent>
        </Card>
        
        {/* Card 5: StayMiddle Fees (Your 5%) */}
        <Card className="col-span-1 hidden md:block">
          <CardHeader className="flex flex-row items-center justify-between p-3 pb-0 space-y-0">
            <CardTitle className="text-sm font-medium">StayMiddle Fees</CardTitle>
            <PercentIcon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent className="p-3 pt-2">
            <div className="text-xl md:text-2xl font-bold">-</div>
            <div className="flex items-center mt-1">
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-50 text-xs">
                Phase 2
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <h2 className="text-lg md:text-xl font-bold mb-3">Pending Approval Items</h2>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Pending Properties */}
        <Card>
          <CardHeader className="p-4 pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Pending Properties</CardTitle>
              <div className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-semibold">
                {pendingProperties} Awaiting
              </div>
            </div>
            <CardDescription className="text-xs">
              Properties waiting for approval
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 pt-2">
            {pendingProperties > 0 ? (
              <div className="space-y-3">
                {properties
                  .filter(property => property.status === PropertyStatus.PENDING)
                  .slice(0, 3)
                  .map(property => (
                    <div key={property.id} className="flex justify-between items-center border-b pb-2">
                      <div className="overflow-hidden">
                        <p className="font-medium text-sm truncate max-w-[150px]">{property.title}</p>
                        <p className="text-xs text-muted-foreground">{property.area}</p>
                      </div>
                      <Button asChild size="sm" className="h-8 text-xs">
                        <Link href={`/admin/properties/${property.id}`}>Review</Link>
                      </Button>
                    </div>
                  ))}
                {pendingProperties > 3 && (
                  <Button asChild variant="outline" className="w-full mt-2 text-xs h-8">
                    <Link href="/admin/properties?status=pending">View All ({pendingProperties})</Link>
                  </Button>
                )}
              </div>
            ) : (
              <div className="text-center py-4">
                <CheckCircle2 className="h-8 w-8 mx-auto text-green-500 mb-2" />
                <h3 className="font-medium text-sm">All Clear!</h3>
                <p className="text-muted-foreground text-xs">No pending properties</p>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Pending Users */}
        <Card>
          <CardHeader className="p-4 pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Pending Users</CardTitle>
              <div className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-semibold">
                {pendingOwners + pendingAgents} Awaiting
              </div>
            </div>
            <CardDescription className="text-xs">
              User accounts waiting for verification
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 pt-2">
            {(pendingOwners + pendingAgents) > 0 ? (
              <div className="space-y-3">
                {owners
                  .filter(owner => owner.status === UserStatus.PENDING)
                  .slice(0, 2)
                  .map(owner => (
                    <div key={owner.id} className="flex justify-between items-center border-b pb-2">
                      <div className="flex items-center overflow-hidden">
                        <div className="w-7 h-7 flex-shrink-0 rounded-full bg-primary/10 flex items-center justify-center mr-2">
                          <UserCheck className="h-3 w-3 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{owner.fullName}</p>
                          <div className="flex items-center flex-wrap">
                            <p className="text-xs text-muted-foreground truncate max-w-[120px] mr-1">{owner.email}</p>
                            <span className="bg-blue-100 text-blue-800 px-1 rounded text-[10px]">Owner</span>
                          </div>
                        </div>
                      </div>
                      <Button asChild size="sm" className="h-8 text-xs ml-2">
                        <Link href={`/admin/users/${owner.id}`}>Review</Link>
                      </Button>
                    </div>
                  ))}
                  
                {agents
                  .filter(agent => agent.status === UserStatus.PENDING)
                  .slice(0, 1)
                  .map(agent => (
                    <div key={agent.id} className="flex justify-between items-center border-b pb-2">
                      <div className="flex items-center overflow-hidden">
                        <div className="w-7 h-7 flex-shrink-0 rounded-full bg-primary/10 flex items-center justify-center mr-2">
                          <Users className="h-3 w-3 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{agent.fullName}</p>
                          <div className="flex items-center flex-wrap">
                            <p className="text-xs text-muted-foreground truncate max-w-[120px] mr-1">{agent.email}</p>
                            <span className="bg-purple-100 text-purple-800 px-1 rounded text-[10px]">Agent</span>
                          </div>
                        </div>
                      </div>
                      <Button asChild size="sm" className="h-8 text-xs ml-2">
                        <Link href={`/admin/users/${agent.id}`}>Review</Link>
                      </Button>
                    </div>
                  ))}
                  
                {(pendingOwners + pendingAgents) > 3 && (
                  <Button asChild variant="outline" className="w-full mt-2 text-xs h-8">
                    <Link href="/admin/users?status=pending">View All ({pendingOwners + pendingAgents})</Link>
                  </Button>
                )}
              </div>
            ) : (
              <div className="text-center py-4">
                <CheckCircle2 className="h-8 w-8 mx-auto text-green-500 mb-2" />
                <h3 className="font-medium text-sm">All Clear!</h3>
                <p className="text-muted-foreground text-xs">No pending users</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}